import { useId, useState } from 'react'
import {
  Box,
  Button,
  Link,
  MenuItem,
  Stack,
  TextField,
  Typography,
  type TextFieldProps,
} from '@mui/material'
import CheckIcon from '@mui/icons-material/Check'
import CloseIcon from '@mui/icons-material/Close'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import { CARD_COLORS } from '@ds/theme/color-mode'
import { APPROVAL_ANSWER_STRINGS } from './strings'

export type TApprovalAnswer = {
  choice: string
  reason: string
  fee: string
  comment: string
  description: string
}

export const NOT_ANSWERED: TApprovalAnswer = {
  choice: '',
  reason: '',
  fee: '',
  comment: '',
  description: '',
}

const CHOICES = ['approve', 'reject', 'counteroffer'] as const

const formatOffered = (offered: string, currency?: string | null) =>
  currency
    ? new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency,
        currencyDisplay: 'narrowSymbol',
      }).format(Number(offered))
    : offered

export const findFeeError = (fee: string, offered?: string | null, currency?: string | null) => {
  if (fee !== '' && Number(fee) < 0) {
    return APPROVAL_ANSWER_STRINGS.min0
  }
  if (
    fee !== '' &&
    offered !== null &&
    offered !== undefined &&
    Math.round(Number(fee) * 100) === Math.round(Number(offered) * 100)
  ) {
    return APPROVAL_ANSWER_STRINGS.sameFee(formatOffered(offered, currency))
  }

  return ''
}

export const keepFeeCents = (previous: string, typed: string) =>
  /\.\d{3}/.test(typed) ? previous : typed

export const isApprovalAnswered = (answer: TApprovalAnswer, offered?: string | null) =>
  answer.choice === 'approve' ||
  (answer.choice === 'reject' &&
    answer.reason !== '' &&
    (answer.reason !== 'other' || answer.description.trim() !== '')) ||
  (answer.choice === 'counteroffer' &&
    answer.fee !== '' &&
    findFeeError(answer.fee, offered) === '')

type TApprovalAnswerFieldsProps = {
  answer: TApprovalAnswer
  offeredPrice?: string | null
  offeredCurrency?: string | null
  disabled?: boolean
  onChange: (change: Partial<TApprovalAnswer>) => void
}

const fieldSx = {
  gap: 0.75,
  fontSize: 14,
  color: 'text.secondary',
  '.MuiInputBase-root': { fontSize: 16, bgcolor: 'background.paper', borderRadius: 1 },
  '.MuiInputBase-input': { px: 1.75, py: 1.5 },
  '.MuiInputBase-multiline': { p: 0 },
  '.MuiFormHelperText-root': { mx: 0, fontSize: 13 },
} as const

const AnswerField = ({
  label,
  maxWidth,
  ...props
}: TextFieldProps & { label: string; maxWidth?: number }) => {
  const id = useId()
  return (
    <Stack sx={{ ...fieldSx, maxWidth }}>
      <Box component="label" htmlFor={id}>
        {label}
      </Box>
      <TextField id={id} {...props} />
    </Stack>
  )
}

const CHOICE_ICONS = {
  approve: <CheckIcon />,
  reject: <CloseIcon />,
  counteroffer: <EditOutlinedIcon />,
} as const

export const ApprovalAnswerFields = ({
  answer,
  offeredPrice,
  offeredCurrency,
  disabled,
  onChange,
}: TApprovalAnswerFieldsProps) => {
  const [commentOpen, setCommentOpen] = useState(false)
  const showComment = answer.choice === 'reject' || commentOpen || answer.comment !== ''
  const feeError = findFeeError(answer.fee, offeredPrice, offeredCurrency)

  return (
    <>
      <Stack direction="row" flexWrap="wrap" gap={1.25} role="group">
        {CHOICES.map(choice => {
          const selected = answer.choice === choice
          const selectedColor = choice === 'reject' ? CARD_COLORS.rejectFill : 'text.primary'
          return (
            <Button
              key={choice}
              aria-pressed={selected}
              disabled={disabled}
              onClick={() => onChange({ choice })}
              startIcon={selected ? CHOICE_ICONS[choice] : undefined}
              sx={{
                minHeight: 44,
                px: 2.5,
                borderRadius: 999,
                border: 1,
                fontSize: 15,
                fontWeight: 600,
                textTransform: 'none',
                borderColor: selected ? selectedColor : 'text.primary',
                bgcolor: selected ? selectedColor : 'background.paper',
                color: selected ? 'background.paper' : 'text.primary',
                '&:hover': {
                  bgcolor: selected ? selectedColor : 'background.paper',
                },
              }}
            >
              {APPROVAL_ANSWER_STRINGS[choice]}
            </Button>
          )
        })}
      </Stack>
      {answer.choice === 'reject' && (
        <Box display="grid" gridTemplateColumns="repeat(2, minmax(0, 1fr))" gap={2}>
          <AnswerField
            label={APPROVAL_ANSWER_STRINGS.reason}
            select
            value={answer.reason}
            disabled={disabled}
            onChange={event => onChange({ reason: event.target.value })}
          >
            {Object.entries(APPROVAL_ANSWER_STRINGS.rejectReasons).map(([reason, label]) => (
              <MenuItem key={reason} value={reason}>
                {label}
              </MenuItem>
            ))}
          </AnswerField>
          {answer.reason === 'other' && (
            <AnswerField
              label={APPROVAL_ANSWER_STRINGS.describeReason}
              value={answer.description}
              disabled={disabled}
              onChange={event => onChange({ description: event.target.value })}
              slotProps={{ htmlInput: { maxLength: 255 } }}
            />
          )}
        </Box>
      )}
      {answer.choice === 'counteroffer' && (
        <>
          <AnswerField
            label={APPROVAL_ANSWER_STRINGS.newFee(offeredCurrency)}
            maxWidth={280}
            type="number"
            value={answer.fee}
            disabled={disabled}
            onChange={event => onChange({ fee: keepFeeCents(answer.fee, event.target.value) })}
            slotProps={{ htmlInput: { step: 0.01 } }}
            error={feeError !== ''}
            helperText={feeError}
          />
          <Typography sx={{ m: 0, fontSize: 13, color: 'text.secondary' }}>
            {APPROVAL_ANSWER_STRINGS.feeHint}
          </Typography>
        </>
      )}
      {showComment && (
        <AnswerField
          label={APPROVAL_ANSWER_STRINGS.comment}
          value={answer.comment}
          disabled={disabled}
          onChange={event => onChange({ comment: event.target.value })}
          slotProps={{ htmlInput: { maxLength: 2000 } }}
          multiline
          minRows={2}
        />
      )}
      {!showComment && (
        <Link
          component="button"
          type="button"
          color="text.primary"
          disabled={disabled}
          onClick={() => setCommentOpen(true)}
          sx={{ alignSelf: 'flex-start', fontSize: 16, minHeight: 44 }}
        >
          {APPROVAL_ANSWER_STRINGS.addComment}
        </Link>
      )}
    </>
  )
}
